<script setup>
import { computed, onMounted, ref, watch, nextTick } from "vue";
import TagApi from "@api/tag.api";
import FormFileUpload from "@/components/form/FormFileUpload.vue";
import { useMetadataStore } from "@store/metadata";
import HandleGroupSelectData from "@/services/HandleGroupSelectData";
import BusinessDomainsApi from "@api/business_domains.api";
import { ElNotification } from "element-plus";
import aiFormProcessor from "@services/aiFormProcessor";

const state = useMetadataStore();
const metadata = computed(() => state.getMetaData);
const options = ref([]);

// const documentTypesForSelect = computed(() => {
//     const raw = metadata.value.documentTypes ?? [];
//     return HandleGroupSelectData(raw) ?? [];
// });

const documentTypesForSelect = computed(() => {
    
    const raw = metadata.value.documentTypes ?? [];
    const allDocumentTypes = HandleGroupSelectData(raw) ?? [];
    
    // Filter document types that are mapped to Category3 (Other Documents - category_id: 3)
    // Use props.categoryMappings instead of making API call
    const category3DocumentTypeIds = props.categoryMappings[4] || [];

    // Filter all document types to only include mapped ones
    return allDocumentTypes.filter(docType => 
        category3DocumentTypeIds.includes(docType.id)
    );
});
const offeringForSelect = computed(() => metadata.value.offering ?? []);
const businessDomainsForSelect = ref([]);
const requiredDomainIds = ref([]);
const businessUnitsForSelect = computed(
    () => metadata.value.businessUnit ?? []
);
const usersForSelect = computed(() => metadata.value.users ?? []);
const industriesForSelect = computed(() => metadata.value.industries ?? []);

const props = defineProps({
    document: {
        type: Object,
    },
    formErrors: {
        type: Object,
    },
    v$: {
        type: Object,
    },
    isUpdateForm: {
        type: Boolean,
    },
    categoryMappings: {
        type: Object,
        default: () => ({})
    },
});

// Use AI form processor service
const isAIProcessing = ref(false);
const isFormReadonly = ref(false);
const aiMode = ref('ai'); // Default to AI mode for documents since file upload is primary

// Initialize form state based on AI mode (only for create forms)
if (!props.document.id && aiMode.value === 'ai') {
    isFormReadonly.value = true; // Disable fields initially in AI mode for create forms only
}

// Tags from metadata and normalized for select
const tagsFromMeta = computed(() => metadata.value.tags || []);
const tagsForSelect = computed(() => {
    return tagsFromMeta.value
        .filter((tag) => tag && tag.name && typeof tag.name === "string")
        .map((tag) => ({ id: tag.id, name: tag.name }));
});

// Inline required validation for files (create mode only)
const filesErrors = computed(() => {
    const errs = Array.isArray(props.formErrors?.files)
        ? [...props.formErrors.files]
        : [];
    const isCreate = !props.document?.id;
    const noFiles =
        !Array.isArray(props.document?.files) ||
        props.document.files.length === 0;
    if (isCreate && noFiles) {
        errs.push("Please upload at least one file.");
    }
    return errs;
});


const onUploadSuccess = async (response) => {
    if (!props.document.files) props.document.files = [];

    // Store full file object (not only ID)
    props.document.files = [{
        id: response.file.id,
        name: response.file.name,
        original_name: response.file.original_name,
        size: response.file.size,
        type: response.file.type,
        uid: response.file.uid || response.file.id,
    }];

    // Trigger AI processing only if AI mode is selected
    if (aiMode.value === 'ai') {
        isAIProcessing.value = true;
        await aiFormProcessor.processAIAnalysis(response.file.id, props.document);
        isAIProcessing.value = false;
        isFormReadonly.value = false;
    }
};


const onUploadDelete = (file) => {
    delete props.document.files;
    isFormReadonly.value = true; // Reset to readonly when file is deleted
};

// Watch for AI mode changes
watch(aiMode, (newMode) => {
    if (newMode === 'manual') {
        // Enable form fields in manual mode
        isFormReadonly.value = false;
        isAIProcessing.value = false;
    } else if (newMode === 'ai' && !props.document.id) {
        // Disable form fields in AI mode (only for create forms)
        isFormReadonly.value = true;
        isAIProcessing.value = false;
    }
});

// Simple tag creation function for form-el-select
const createTag = async (tagName) => {
    try {
        // Create the tag in the database (firstOrCreate handles duplicates)
        const response = await TagApi.store({ name: tagName });

        // Add the new tag to the metadata store
        const newTag = {
            id: response.data.data.tag.id,
            name: response.data.data.tag.name,
        };

        // Ensure the newly created tag is selected by default
        if (!Array.isArray(props.document.tags)) {
            props.document.tags = [];
        }

        // If a temporary label was added by the select, replace it with the new ID
        const lastIdx = props.document.tags.lastIndexOf(tagName);
        if (lastIdx !== -1) {
            props.document.tags.splice(lastIdx, 1, newTag.id);
        } else if (!props.document.tags.includes(newTag.id)) {
            props.document.tags.push(newTag.id);
        }

        if (!state.metadata.tags) {
            state.metadata.tags = [];
        }

        const existingTag = state.metadata.tags.find((t) => t.id == newTag.id);

        if (!existingTag) {
            state.metadata.tags.push(newTag);
        }
        console.log("dddddddeeeeddddddd", newTag);
        return newTag;
    } catch (error) {


        // Show user-friendly error message
        const errorMessage = error.response?.data?.errors.message;

        let errorTxt = '';
        if (typeof errorMessage === 'string') {
            errorTxt = errorMessage
        } else {

            errorMessage.forEach(element => {
                errorTxt += element + '\n'
            });
        }
        // Use ElNotification for better UX
        ElNotification({
            title: 'Error',
            message: errorTxt,
            type: 'error',
            position: 'bottom-right',
            duration: 5000,
        });

        throw error;
    }
};

const remoteMethod = (query) => {
    if (query) {
        TagApi.list({ Keyword: query, page: 0, limit: 100 }).then((res) => {
            options.value = res.data.data
                .filter((item) => {
                    return item.name
                        .toLowerCase()
                        .includes(query.toLowerCase());
                })
                .map((item) => {
                    return {
                        value: item.id,
                        label: item.name,
                    };
                });
        });
    } else {
        options.value = [];
    }
};

watch(
    () => props.document.offering_ids,
    (newValue) => {
        if (!newValue || newValue.length === 0) {
            businessDomainsForSelect.value = [];
            props.document.business_domain_ids = [];
            requiredDomainIds.value = [];
            return;
        }
       
        BusinessDomainsApi.getDomainsByOfferings({offering_ids: newValue}).then((response) => {
            businessDomainsForSelect.value = [];
            response.data.data.domains.forEach(domain => {
                businessDomainsForSelect.value.push({
                    id: domain.id,
                    name: domain.name
                });
            });
            
            // Store required domain IDs
            requiredDomainIds.value = businessDomainsForSelect.value.map(domain => domain.id);
            
            // Update business_domain_ids, but preserve any that are still required
            const currentDomainIds = props.document.business_domain_ids || [];
            const newDomainIds = businessDomainsForSelect.value.map(domain => domain.id);
            
            // Keep domains that are still required, add new required ones
            const preservedDomains = currentDomainIds.filter(id => newDomainIds.includes(id));
            props.document.business_domain_ids = [...new Set([...preservedDomains, ...newDomainIds])];
        });
    }
);

function handleBusinessDomainDeselected(domainId) {
    if (requiredDomainIds.value.includes(domainId)) {
        nextTick(() => {
            if (!props.document.business_domain_ids) {
                props.document.business_domain_ids = [];
            }
            // Ensure the domain is in the array
            if (!props.document.business_domain_ids.includes(domainId)) {
                props.document.business_domain_ids.push(domainId);
            }
        });
    }
}

watch(
    () => props.document.business_domain_ids,
    (newValue, oldValue) => {
        // Only check if we have required domains
        if (requiredDomainIds.value.length === 0) {
            return;
        }
        
        // Check if any required domain was removed
        const removedDomains = (oldValue || []).filter(id => 
            requiredDomainIds.value.includes(id) && !(newValue || []).includes(id)
        );
        
        if (removedDomains.length > 0) {
            // Restore removed required domains
            nextTick(() => {
                if (!props.document.business_domain_ids) {
                    props.document.business_domain_ids = [];
                }
                // Add back any required domains that were removed
                removedDomains.forEach(domainId => {
                    if (!props.document.business_domain_ids.includes(domainId)) {
                        props.document.business_domain_ids.push(domainId);
                    }
                });
            });
        }
    },
    { deep: true }
);

onMounted(() => {
    remoteMethod();
    if (!props.isUpdateForm) {
        props.document.tags = [];
    }
    
    // In edit mode, populate business domains
    if (props.isUpdateForm) {
        // First, check if document has businessDomains relationship loaded
        if (props.document?.businessDomains && Array.isArray(props.document.businessDomains)) {
            businessDomainsForSelect.value = props.document.businessDomains.map(domain => ({
                id: domain.id,
                name: domain.name
            }));
        }
        
        // Then fetch required domains based on current offerings
        if (props.document.offering_ids && props.document.offering_ids.length > 0) {
            BusinessDomainsApi.getDomainsByOfferings({offering_ids: props.document.offering_ids}).then((response) => {
                const domains = response.data.data.domains || [];
                requiredDomainIds.value = domains.map(domain => domain.id);
                
                // Update businessDomainsForSelect with domains from API
                businessDomainsForSelect.value = domains.map(domain => ({
                    id: domain.id,
                    name: domain.name
                }));
            });
        }
    }
});
</script>

<template>
    <div>
        <!-- AI/Manual Mode Selection -->
        <div class="row my-2" v-if="!document.id">
            <div class="col-md-12">
                <label class="form-label mb-3">
                    <strong>How would you like to process this document?</strong>
                </label>
                <div class="d-flex gap-3">
                    <el-radio v-model="aiMode" label="manual">
                        <i class="fas fa-keyboard me-2"></i>
                        Manual Entry
                    </el-radio>
                    <el-radio v-model="aiMode" label="ai">
                        <i class="fas fa-robot me-2"></i>
                        AI-Powered (from uploaded file)
                    </el-radio>
                </div>
            </div>
        </div>

        <template v-if="!document.id">
            <div class="row my-2">
                <div class="col-md-12">
                    <label class="form-label"> Upload Files </label>
                    <FormFileUpload
                        :allowed-files="'.jpg, .jpeg, .png, .pdf, .doc, .docx, .xls, .xlsx, .csv, .ppt, .pptx, .mp4, .mov, .vsdx'"
                        :errors="filesErrors"
                        :limit="10"
                         :model="document"
                        name="files"
                        :multiple="false"
                        :required="true"
                        clearable
                        label="Upload Files"
                        type="document_attachment"
                        @successUpload="onUploadSuccess"
                        @successDeleteUpload="onUploadDelete"
                    >
                    </FormFileUpload>
                    <small
                        v-for="error in filesErrors || []"
                        class="text-danger"
                        style="font-size: 0.8rem; top: 40px"
                    >
                        {{ error }}
                    </small>
                </div>
            </div>
        </template>

        <!-- Title + Type -->
        <div class="row my-2">
            <div class="col-md-6">
                <form-input
                    id="name"
                    :model="document"
                  v-model="document.title"
                    :errors="v$.title"
                    :label="$t('fields.document_title')"
                    :required="true"
                    placeholder="What is this document about?"
                    type="text"
                    :readonly="isFormReadonly"
                />
            </div>
            <div class="col-md-6">
                <group-form-el-select
                    :errors="formErrors"
                    :label="$t('fields.document_types')"
                    :model="document"
                    :options="documentTypesForSelect"
                    :required="true"
                    clearable
                    label-field="name"
                    group-by-field="group"
                    name="document_type_id"
                    placeholder="Select whether this is a Case Study, EM Guide, CXO etc"
                    :disabled="isFormReadonly"
                />
            </div>
        </div>

        <!-- Offering + Domain -->
        <div class="row my-2">
            <div class="col-md-6">
                <form-el-select
                    :errors="v$.offering_ids"
                    :label="$t('fields.offering_title')"
                    :model="document"
                    :multiple="true"
                    :options="offeringForSelect"
                    :required="true"
                    clearable
                    label-field="fields.offering_id"
                    name="offering_ids"
                    placeholder="Select the Offering that best fits the project"
                    :disabled="isFormReadonly"
                />
                <small
                    v-for="error in v$.offering_ids.$errors"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
            <div class="col-md-6">
                <form-el-select
                    :errors="v$.business_domain_ids"
                    :label="$t('fields.business_domain')"
                    :model="document"
                    :multiple="true"
                    :options="businessDomainsForSelect"
                    :required="true"
                    :disabled="isFormReadonly"
                    :clearable="false"
                    readonly
                    label-field="fields.business_domain"
                    name="business_domain_ids"
                    placeholder="Selected based on Service offering"
                    @deselected="handleBusinessDomainDeselected"
                />
                <small
                    v-for="error in v$.business_domain_ids.$errors"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
        </div>

        <!-- Authors + BU -->
        <div class="row my-2">
            <div class="col-md-6">
                <form-el-select
                    :errors="v$.authors_ids"
                    :label="$t('fields.authors')"
                    :model="document"
                    :multiple="true"
                    :options="usersForSelect"
                    :required="true"
                    clearable
                    label-field="fields.team_ids"
                    name="authors_ids"
                    placeholder="Select the TAMers who worked on creating this document."
                />
                <small
                    v-for="error in v$.authors_ids.$errors"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
            <div class="col-md-6">
                <form-el-select
                    :errors="v$.business_unit_id"
                    :label="$t('fields.business_unit')"
                    :model="document"
                    :options="businessUnitsForSelect"
                    :required="true"
                    clearable
                    label-field="fields.business_unit_id"
                    name="business_unit_id"
                    placeholder="Choose all Business Units involved (e.g., Advisory, Digital)"
                />
                <small
                    v-for="error in v$.business_unit_id.$errors"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
        </div>

        <!-- Context + Purpose -->
        <div class="row my-2">
            <div class="col-md-12 mb-3">
                <form-tiptap-text-area
                    id="name"
                  v-model="document.document_context"
                    :errors="v$.document_context"
                    :label="$t('fields.document_context')"
                    :required="true"
                    placeholder="When/why this was created?"
                    rows="4"
                    :is-disabled="isFormReadonly"
                />
            </div>
            <div class="col-md-12 mb-3">
                <form-tiptap-text-area
                    id="name"
                     v-model="document.document_purpose"
                    :errors="v$?.document_purpose"
                    :label="$t('fields.document_purpose')"
                    :required="true"
                    placeholder="How will it help TAMers in future?"
                    rows="4"
                    :is-disabled="isFormReadonly"
                />
            </div>

            <div class="col-md-6">
                <form-el-select
                    :errors="v$.industry_id"
                    :label="$t('fields.industry')"
                    :model="document"
                    :options="industriesForSelect"
                    :required="true"
                    :disabled="isFormReadonly"
                    clearable
                    label-field="fields.industry_id"
                    name="industry_id"
                    placeholder="Select the primary sector the document relates to (e.g., Culture, Tourism, Sports, Healthcare, Technology, etc.)"
                />
                <small
                    v-for="error in v$.industry_id.$errors"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="'Tags'"
                    :model="document"
                    :multiple="true"
                    :options="tagsForSelect"
                    :required="false"
                    :allow-create="true"
                    :on-create-tag="createTag"
                    clearable
                    label-field="name"
                    name="tags"
                    placeholder="AI generated Smart Tags"
                    :disabled="isFormReadonly"
                />
                <small
                    v-for="(error, index) in formErrors?.tags || []"
                    :key="index"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error }}
                </small>
            </div>
        </div>
    </div>
</template>
